// API Service Layer for My Account
import type {
    AccountUpdateData,
    Order,
    OrderTrackingItem,
    Invoice,
    AccountDashboardData,
    MenuItem,
    UserData,
    CreditSummary,
    DraftOrder,
    DraftOrderDetail,
    DraftOrderItemInput,
    CustomerAddress,
    Quote,
    QuoteFulfillmentOptionsResponse,
    QuoteItemInput,
    QuoteShippingOptionsResponse,
    WooCommerceResponse,
    OrdersParams,
    InvoicePaymentOrderResponse
} from '../types/myaccount';

// Get WordPress REST API nonce from global object
declare global {
    interface Window {
        wpApiSettings?: {
            root?: string;
            nonce?: string;
        };
        wekaApiSettings?: {
            root?: string;
            nonce?: string;
            api_endpoint_base?: string;
        };
        wc_add_to_cart_params?: any;
    }
}

const DEFAULT_API_BASE = '/wp-json/';
let API_NONCE = String(window.wpApiSettings?.nonce || window.wekaApiSettings?.nonce || '').trim();

const normalizeApiBase = (value?: string): string => {
    const normalized = String(value || '').trim();
    if (normalized === '') {
        return '';
    }

    return normalized.endsWith('/') ? normalized : `${normalized}/`;
};

const getApiBase = (): string => {
    const wpApiRoot = normalizeApiBase(window.wpApiSettings?.root);
    if (wpApiRoot !== '') {
        return wpApiRoot;
    }

    const wekaApiRoot = normalizeApiBase(window.wekaApiSettings?.root);
    if (wekaApiRoot !== '') {
        return wekaApiRoot;
    }

    return DEFAULT_API_BASE;
};

const getApiNonce = (): string => {
    const wpNonce = String(window.wpApiSettings?.nonce || '').trim();
    if (wpNonce !== '') {
        API_NONCE = wpNonce;
        return wpNonce;
    }

    const wekaNonce = String(window.wekaApiSettings?.nonce || '').trim();
    if (wekaNonce !== '') {
        API_NONCE = wekaNonce;
        return wekaNonce;
    }

    return API_NONCE;
};

const setApiNonce = (nonce: string): void => {
    const normalized = String(nonce || '').trim();
    if (normalized === '') {
        return;
    }

    API_NONCE = normalized;
    window.wpApiSettings = {
        ...(window.wpApiSettings || {}),
        root: window.wpApiSettings?.root || getApiBase(),
        nonce: normalized,
    };
    window.wekaApiSettings = {
        ...(window.wekaApiSettings || {}),
        root: window.wekaApiSettings?.root || getApiBase(),
        nonce: normalized,
    };
};

const sanitizeApiErrorMessage = (message: string): string => {
    const stripped = String(message || '')
        .replace(/<[^>]+>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

    if (stripped === '') {
        return '';
    }

    if (stripped.toLowerCase().includes('there has been a critical error on this website')) {
        return 'The server hit an internal error while processing this request.';
    }

    return stripped;
};

class ApiService {
    private cache = new Map<string, { data: any; timestamp: number }>();
    private inFlightRequests = new Map<string, Promise<any>>();
    private failureState = new Map<string, { count: number; lastFailedAt: number }>();
    private readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutes cache (increased from 30 seconds)
    private readonly REQUEST_TIMEOUT = 30000; // 30 second timeout
    private readonly MAX_NONCE_REFRESH_RETRIES = 1;
    private readonly FAILURE_COOLDOWN_MS = 10000;
    private readonly MAX_CONSECUTIVE_FAILURES = 5;
    private nonceRefreshPromise: Promise<void> | null = null;

    private async request<T>(
        endpoint: string,
        options: RequestInit = {},
        useCache = false,
        cacheTTL?: number,
        retryCount = 0
    ): Promise<T> {
        const url = `${getApiBase()}${endpoint}`;
        const cacheKey = `${url}_${JSON.stringify(options)}`;
        const ttl = cacheTTL || this.CACHE_TTL;
        const method = options.method || 'GET';
        const now = Date.now();

        // Check cache
        if (useCache && this.cache.has(cacheKey)) {
            const cached = this.cache.get(cacheKey)!;
            if (Date.now() - cached.timestamp < ttl) {
                return cached.data;
            }
            this.cache.delete(cacheKey);
        }

        // Dedupe concurrent requests to the same GET endpoint/options
        if (method === 'GET' && this.inFlightRequests.has(cacheKey)) {
            return this.inFlightRequests.get(cacheKey) as Promise<T>;
        }

        // Circuit breaker: if an endpoint keeps failing rapidly, short-circuit briefly
        const failed = this.failureState.get(cacheKey);
        if (failed && failed.count >= this.MAX_CONSECUTIVE_FAILURES) {
            const retryAt = failed.lastFailedAt + this.FAILURE_COOLDOWN_MS;
            if (now < retryAt) {
                throw new Error('Temporarily paused repeated failed requests. Please try again shortly.');
            }
        }

        const headers: Record<string, string> = {
            'Content-Type': 'application/json',
        };
        
        // Only add nonce if it exists
        const nonce = getApiNonce();
        if (nonce) {
            headers['X-WP-Nonce'] = nonce;
        }

        const defaultOptions: RequestInit = {
            headers,
            credentials: 'same-origin',
        };

        // Create abort controller for timeout
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), this.REQUEST_TIMEOUT);

        const requestPromise = (async (): Promise<T> => {
            try {
                const response = await fetch(url, {
                    ...defaultOptions,
                    ...options,
                    headers: {
                        ...defaultOptions.headers,
                        ...options.headers,
                    },
                    signal: controller.signal,
                });

                clearTimeout(timeoutId);

                if (!response.ok) {
                    let errorMessage = `API Error: ${response.statusText}`;

                    try {
                        const errorPayload = await response.clone().json();
                        if (typeof errorPayload?.message === 'string' && errorPayload.message.trim() !== '') {
                            errorMessage = sanitizeApiErrorMessage(errorPayload.message) || errorMessage;
                        } else if (typeof errorPayload?.error === 'string' && errorPayload.error.trim() !== '') {
                            errorMessage = sanitizeApiErrorMessage(errorPayload.error) || errorMessage;
                        }
                    } catch {
                        // Ignore non-JSON error bodies and keep the status text fallback.
                    }

                    // Check for 403 Forbidden which indicates expired nonce
                    if (response.status === 403) {
                        // Try nonce refresh once with explicit retry cap to prevent loops
                        if (!endpoint.includes('refresh-nonce') && retryCount < this.MAX_NONCE_REFRESH_RETRIES) {
                            const refreshed = await this.refreshNonce();
                            if (refreshed) {
                                return this.request<T>(endpoint, options, useCache, cacheTTL, retryCount + 1);
                            }
                        }
                    }
                    throw new Error(errorMessage);
                }

                const data = await response.json();

                // Cache successful GET requests
                if (useCache && (!options.method || options.method === 'GET')) {
                    this.cache.set(cacheKey, { data, timestamp: Date.now() });
                }

                this.failureState.delete(cacheKey);
                return data;
            } catch (error: any) {
                clearTimeout(timeoutId);
                const prev = this.failureState.get(cacheKey);
                this.failureState.set(cacheKey, {
                    count: prev ? prev.count + 1 : 1,
                    lastFailedAt: Date.now(),
                });
                if (error.name === 'AbortError') {
                    throw new Error('Request timeout - please check your connection');
                }
                throw error;
            } finally {
                this.inFlightRequests.delete(cacheKey);
            }
        })();

        if (method === 'GET') {
            this.inFlightRequests.set(cacheKey, requestPromise);
        }

        return requestPromise;
    }

    /**
     * Refresh the WordPress REST API nonce
     */
    private async refreshNonce(): Promise<boolean> {
        // Prevent multiple simultaneous refresh attempts
        if (this.nonceRefreshPromise) {
            await this.nonceRefreshPromise;
            return Boolean(API_NONCE);
        }

        let refreshed = false;
        this.nonceRefreshPromise = (async () => {
            try {
                // Use WordPress heartbeat API to get fresh nonce
                const response = await fetch(`${getApiBase()}weka/v1/refresh-nonce`, {
                    method: 'POST',
                    credentials: 'same-origin',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                });

                if (response.ok) {
                    const data = await response.json();
                    if (data.nonce) {
                        setApiNonce(data.nonce);
                        refreshed = true;
                    }
                }
            } catch (error) {
                console.error('Failed to refresh nonce:', error);
            } finally {
                this.nonceRefreshPromise = null;
            }
        })();

        await this.nonceRefreshPromise;
        return refreshed;
    }

    clearCache(pattern?: string): void {
        if (pattern) {
            // Clear cache entries matching pattern
            for (const key of this.cache.keys()) {
                if (key.includes(pattern)) {
                    this.cache.delete(key);
                }
            }
        } else {
            this.cache.clear();
        }
    }

    invalidateCache(endpoint: string): void {
        // Invalidate cache for specific endpoint
        this.clearCache(endpoint);
    }

    // Orders API
    async getOrders(params?: OrdersParams): Promise<Order[]> {
        const queryParams = new URLSearchParams();
        if (params?.page) queryParams.append('page', params.page.toString());
        if (params?.per_page) queryParams.append('per_page', params.per_page.toString());
        if (params?.status) queryParams.append('status', params.status);
        if (params?.customer_id) queryParams.append('customer', params.customer_id.toString());
        if (params?.kind) queryParams.append('kind', params.kind);

        const query = queryParams.toString() ? `?${queryParams.toString()}` : '';
        return this.request<Order[]>(`weka/v1/account/orders${query}`, {}, true);
    }

    async getOrder(orderId: number): Promise<Order> {
        return this.request<Order>(`weka/v1/account/orders/${orderId}`, {}, true);
    }

    async getAccountDashboardData(): Promise<AccountDashboardData> {
        return this.request<AccountDashboardData>('weka/v1/account/dashboard', {}, true);
    }

    async getOrderTracking(orderId: number): Promise<OrderTrackingItem[]> {
        return this.request<OrderTrackingItem[]>(
            `wc-shipment-tracking/v3/orders/${orderId}/shipment-trackings`,
            {},
            false
        );
    }

    // Draft Orders API
    async getDraftOrders(): Promise<DraftOrder[]> {
        const response = await this.request<WooCommerceResponse<DraftOrder[]>>('weka/v1/draft-orders', {}, true);
        if (response.success !== true) {
            throw new Error(response.message || 'Failed to load draft orders');
        }

        return Array.isArray(response.data) ? response.data : [];
    }

    async getDraftOrder(draftId: number): Promise<DraftOrderDetail> {
        const response = await this.request<WooCommerceResponse<DraftOrderDetail>>(
            `weka/v1/draft-orders/${draftId}`,
            {},
            true
        );

        if (response.success !== true || !response.data || typeof response.data !== 'object') {
            throw new Error(response.message || 'Failed to load draft order');
        }

        return response.data;
    }

    async createDraftOrder(payload: {
        draft_name?: string;
        items?: DraftOrderItemInput[];
        mode?: 'manual' | 'cart';
    }): Promise<{ draft_id: number; draft: DraftOrderDetail; message: string }> {
        const body: Record<string, unknown> = {};
        if (typeof payload.draft_name === 'string') {
            body.draft_name = payload.draft_name;
        }
        if (payload.mode) {
            body.mode = payload.mode;
        }
        if (Array.isArray(payload.items)) {
            body.items = payload.items;
            if (!body.mode) {
                body.mode = 'manual';
            }
        }

        const response = await this.request<WooCommerceResponse<DraftOrderDetail> & { draft_id?: number }>(
            'weka/v1/draft-orders',
            {
                method: 'POST',
                body: JSON.stringify(body),
            }
        );

        if (response.success !== true || !response.data || typeof response.data !== 'object') {
            throw new Error(response.message || 'Failed to save draft order');
        }

        this.invalidateCache('weka/v1/draft-orders');

        return {
            draft_id: Number(response.draft_id || response.data.id || 0),
            draft: response.data,
            message: response.message || 'Draft saved successfully',
        };
    }

    async updateDraftOrder(draftId: number, payload: { draft_name: string }): Promise<{ draft: DraftOrderDetail; message: string }> {
        const response = await this.request<WooCommerceResponse<DraftOrderDetail>>(
            `weka/v1/draft-orders/${draftId}`,
            {
                method: 'PATCH',
                body: JSON.stringify(payload),
            }
        );

        if (response.success !== true || !response.data || typeof response.data !== 'object') {
            throw new Error(response.message || 'Failed to update draft order');
        }

        this.invalidateCache('weka/v1/draft-orders');

        return {
            draft: response.data,
            message: response.message || 'Draft updated successfully',
        };
    }

    async addDraftOrderItem(draftId: number, payload: DraftOrderItemInput): Promise<{ draft: DraftOrderDetail; message: string }> {
        const response = await this.request<WooCommerceResponse<DraftOrderDetail>>(
            `weka/v1/draft-orders/${draftId}/items`,
            {
                method: 'POST',
                body: JSON.stringify(payload),
            }
        );

        if (response.success !== true || !response.data || typeof response.data !== 'object') {
            throw new Error(response.message || 'Failed to add item to draft order');
        }

        this.invalidateCache('weka/v1/draft-orders');

        return {
            draft: response.data,
            message: response.message || 'Item added to draft successfully',
        };
    }

    async updateDraftOrderItem(
        draftId: number,
        itemKey: string,
        payload: { quantity: number }
    ): Promise<{ draft: DraftOrderDetail; message: string }> {
        const response = await this.request<WooCommerceResponse<DraftOrderDetail>>(
            `weka/v1/draft-orders/${draftId}/items/${encodeURIComponent(itemKey)}`,
            {
                method: 'PATCH',
                body: JSON.stringify(payload),
            }
        );

        if (response.success !== true || !response.data || typeof response.data !== 'object') {
            throw new Error(response.message || 'Failed to update draft item');
        }

        this.invalidateCache('weka/v1/draft-orders');

        return {
            draft: response.data,
            message: response.message || 'Draft item updated successfully',
        };
    }

    async removeDraftOrderItem(draftId: number, itemKey: string): Promise<{ draft: DraftOrderDetail; message: string }> {
        const response = await this.request<WooCommerceResponse<DraftOrderDetail>>(
            `weka/v1/draft-orders/${draftId}/items/${encodeURIComponent(itemKey)}`,
            {
                method: 'DELETE',
            }
        );

        if (response.success !== true || !response.data || typeof response.data !== 'object') {
            throw new Error(response.message || 'Failed to remove draft item');
        }

        this.invalidateCache('weka/v1/draft-orders');

        return {
            draft: response.data,
            message: response.message || 'Draft item removed successfully',
        };
    }

    async loadDraftOrder(draftId: number): Promise<{ cart_url: string; message: string }> {
        const response = await this.request<WooCommerceResponse<null> & { cart_url?: string }>(
            `weka/v1/draft-orders/${draftId}/load`,
            {
                method: 'POST',
            }
        );

        if (response.success !== true || typeof response.cart_url !== 'string' || response.cart_url === '') {
            throw new Error(response.message || 'Failed to load draft order');
        }

        return {
            cart_url: response.cart_url,
            message: response.message || 'Draft loaded to cart successfully',
        };
    }

    async deleteDraftOrder(draftId: number): Promise<{ message: string }> {
        const response = await this.request<WooCommerceResponse<null>>(`weka/v1/draft-orders/${draftId}`, {
            method: 'DELETE',
        });

        if (response.success !== true) {
            throw new Error(response.message || 'Failed to delete draft order');
        }

        this.invalidateCache('weka/v1/draft-orders');

        return {
            message: response.message || 'Draft deleted successfully',
        };
    }

    async convertDraftOrder(draftId: number): Promise<{ checkout_url: string; message: string }> {
        const response = await this.request<WooCommerceResponse<null> & { checkout_url?: string }>(
            `weka/v1/draft-orders/${draftId}/convert`,
            {
                method: 'POST',
            }
        );

        if (response.success !== true || typeof response.checkout_url !== 'string' || response.checkout_url === '') {
            throw new Error(response.message || 'Failed to prepare draft for checkout');
        }

        this.invalidateCache('weka/v1/draft-orders');

        return {
            checkout_url: response.checkout_url,
            message: response.message || 'Draft ready for checkout',
        };
    }

    // Invoices API
    async getInvoices(): Promise<Invoice[]> {
        try {
            return await this.request<Invoice[]>('weka/v1/invoices', {}, true);
        } catch (error) {
            // Silently handle 404 - invoice endpoint may not exist yet
            console.warn('Invoice endpoint not available - returning empty array');
            return [];
        }
    }

    async getInvoice(invoiceId: number): Promise<Invoice> {
        return this.request<Invoice>(`weka/v1/invoices/${invoiceId}`, {}, true);
    }

    // Invoice Payment API
    async createInvoicePayment(invoiceIds: string[]): Promise<InvoicePaymentOrderResponse> {
        return this.request<InvoicePaymentOrderResponse>('weka/v1/invoices/payment-order', {
            method: 'POST',
            body: JSON.stringify({ invoice_ids: invoiceIds }),
        });
    }

    async getUnpaidInvoices(): Promise<any> {
        return this.request<any>('weka/v1/invoice-payment/unpaid', {}, true);
    }

    // Quote API
    async getQuotes(): Promise<Quote[]> {
        const response = await this.request<any>('weka/v1/quotes', {}, true);
        return response.quotes || [];
    }

    async getQuote(quoteId: number): Promise<Quote> {
        const response = await this.request<any>(`weka/v1/quotes/${quoteId}`);
        return response.quote;
    }

    async createQuote(data: any): Promise<any> {
        return this.request<any>('weka/v1/quotes', {
            method: 'POST',
            body: JSON.stringify(data),
        });
    }

    async updateQuote(quoteId: number, data: any): Promise<any> {
        return this.request<any>(`weka/v1/quotes/${quoteId}`, {
            method: 'PUT',
            body: JSON.stringify(data),
        });
    }

    async deleteQuote(quoteId: number): Promise<any> {
        return this.request<any>(`weka/v1/quotes/${quoteId}`, {
            method: 'DELETE',
        });
    }

    async completeQuote(quoteId: number): Promise<any> {
        return this.request<any>(`weka/v1/quotes/${quoteId}/checkout-order`, {
            method: 'POST',
        });
    }

    async getQuoteFulfillmentOptions(): Promise<QuoteFulfillmentOptionsResponse> {
        return this.request<QuoteFulfillmentOptionsResponse>('weka/v1/quotes/fulfillment-options');
    }

    async getQuoteShippingOptions(
        items: QuoteItemInput[],
        options: {
            billingAddressId?: number;
            quoteId?: number;
            shippingAddressId?: number;
            useBillingAsShipping?: boolean;
        } = {}
    ): Promise<QuoteShippingOptionsResponse> {
        return this.request<QuoteShippingOptionsResponse>('weka/v1/quotes/shipping-options', {
            method: 'POST',
            body: JSON.stringify({
                items,
                ...(options.billingAddressId !== undefined ? {billing_address_id: options.billingAddressId} : {}),
                ...(options.quoteId !== undefined ? {quote_id: options.quoteId} : {}),
                ...(options.shippingAddressId !== undefined ? {shipping_address_id: options.shippingAddressId} : {}),
                ...(options.useBillingAsShipping !== undefined ? {use_billing_as_shipping: options.useBillingAsShipping} : {}),
            }),
        });
    }

    // User/Customer API
    async getCurrentUser(): Promise<UserData> {
        return this.request<UserData>('weka/v1/account/current');
    }

    async updateCurrentUser(data: AccountUpdateData): Promise<UserData> {
        return this.request<UserData>('weka/v1/account/current', {
            method: 'POST',
            body: JSON.stringify(data),
        });
    }

    // Address Management API
    async getAddresses(): Promise<CustomerAddress[]> {
        return this.request<CustomerAddress[]>('weka/v1/addresses');
    }

    async getAddress(addressId: number): Promise<CustomerAddress> {
        return this.request<CustomerAddress>(`weka/v1/addresses/${addressId}`);
    }

    async createAddress(address: CustomerAddress): Promise<CustomerAddress> {
        return this.request<CustomerAddress>('weka/v1/addresses', {
            method: 'POST',
            body: JSON.stringify(address),
        });
    }

    async updateAddress(addressId: number, address: Partial<CustomerAddress>): Promise<CustomerAddress> {
        return this.request<CustomerAddress>(`weka/v1/addresses/${addressId}`, {
            method: 'PUT',
            body: JSON.stringify(address),
        });
    }

    async deleteAddress(addressId: number): Promise<void> {
        return this.request<void>(`weka/v1/addresses/${addressId}`, {
            method: 'DELETE',
        });
    }

    // Credit Summary API
    async getCreditSummary(): Promise<CreditSummary> {
        return this.request<CreditSummary>('weka/v1/credit-summary');
    }

    // Menu Items API
    async getMenuItems(): Promise<MenuItem[]> {
        return this.request<MenuItem[]>('weka/v1/my-account/menu-items');
    }

    // Sync Operations
    async syncPortalAddress(): Promise<WooCommerceResponse<any>> {
        return this.request<WooCommerceResponse<any>>('weka/v1/my-account/sync-portal-address', {
            method: 'POST',
        });
    }

    async syncLocationToPortal(locationId: string): Promise<WooCommerceResponse<any>> {
        return this.request<WooCommerceResponse<any>>('weka/v1/my-account/sync-location-to-portal', {
            method: 'POST',
            body: JSON.stringify({ location_id: locationId }),
        });
    }
}

export const api = new ApiService();
export default api;
